import { useState } from 'react'
import { useSessions } from '../hooks/useSessions'
import { useProfile } from '../hooks/useProfile'
import { formatEur, formatKwh, formatDateTimeLocal } from '../utils/formatters'
import { useTranslation } from 'react-i18next'
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts'
import { format, parseISO, subMonths } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

const TZ = 'Europe/Helsinki'

interface Interval {
  energyKwh: number
  spotPriceSntKwh: number
  marginSntKwh: number
}

interface Session {
  startTime: string
  endTime?: string
  totalEnergyKwh: number
  totalCostEur: number
  costCalculated: boolean
  priceEstimated: boolean
  intervals?: string
}

function parseIntervals(json?: string): Interval[] {
  if (!json) return []
  try { return JSON.parse(json) } catch { return [] }
}

function sessionCostBreakdown(s: Session) {
  const ivs = parseIntervals(s.intervals)
  const spotEur = ivs.reduce((sum, iv) => sum + iv.energyKwh * iv.spotPriceSntKwh / 100, 0)
  const marginEur = ivs.reduce((sum, iv) => sum + iv.energyKwh * iv.marginSntKwh / 100, 0)
  return { spotEur, marginEur }
}

export function DashboardPage() {
  const { t } = useTranslation()
  const { sessions, loading, error } = useSessions()
  const { profile } = useProfile()

  if (loading) return <p className="text-brand-500 py-8 text-center">{t('dashboard.loading')}</p>
  if (error) return <p className="text-red-500 py-8 text-center">{error}</p>

  const typed = sessions as unknown as Session[]

  // Summary with cost breakdown
  const totalKwh = typed.reduce((s, x) => s + x.totalEnergyKwh, 0)
  const totalEur = typed.reduce((s, x) => s + x.totalCostEur, 0)
  const hasEstimated = typed.some(s => s.priceEstimated)
  const totals = typed.reduce((acc, s) => {
    const b = sessionCostBreakdown(s)
    return { spotEur: acc.spotEur + b.spotEur, marginEur: acc.marginEur + b.marginEur }
  }, { spotEur: 0, marginEur: 0 })

  // Monthly aggregation for chart
  const monthlyMap = new Map<string, { kwh: number; spotEur: number; marginEur: number; count: number }>()
  for (let i = 5; i >= 0; i--) {
    const d = subMonths(new Date(), i)
    const key = format(d, 'yyyy-MM')
    monthlyMap.set(key, { kwh: 0, spotEur: 0, marginEur: 0, count: 0 })
  }
  for (const s of typed) {
    const local = toZonedTime(parseISO(s.endTime || s.startTime), TZ)
    const key = format(local, 'yyyy-MM')
    const entry = monthlyMap.get(key)
    if (entry) {
      entry.kwh += s.totalEnergyKwh
      entry.count++
      const b = sessionCostBreakdown(s)
      entry.spotEur += b.spotEur
      entry.marginEur += b.marginEur
    }
  }
  const chartData = [...monthlyMap.entries()].map(([month, v]) => ({
    month: month.slice(5), // MM
    kwh: Math.round(v.kwh * 100) / 100,
    spotEur: Math.round(v.spotEur * 100) / 100,
    marginEur: Math.round(v.marginEur * 100) / 100,
    count: v.count,
  }))

  // Recent 5 — sorted by endTime (consistent with billing)
  const recent = [...typed].sort((a, b) => (b.endTime || b.startTime).localeCompare(a.endTime || a.startTime)).slice(0, 5)

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-brand-700">{t('nav.dashboard')}</h1>

      {/* User profile */}
      {profile && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-lg">
              {profile.displayName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-brand-700">{profile.displayName}</p>
              <p className="text-brand-400 text-xs">{profile.email}</p>
            </div>
            <div className="hidden sm:flex gap-6 text-sm text-brand-600">
              {profile.apartmentNumber && (
                <div>
                  <p className="text-brand-400 text-xs">{t('dashboard.apartment')}</p>
                  <p className="font-medium">{profile.apartmentNumber}</p>
                </div>
              )}
              {profile.phone && (
                <div>
                  <p className="text-brand-400 text-xs">{t('dashboard.phone')}</p>
                  <p className="font-medium">{profile.phone}</p>
                </div>
              )}
              {profile.parkingSpaceNumber && (
                <div>
                  <p className="text-brand-400 text-xs">{t('dashboard.parking')}</p>
                  <p className="font-medium">{profile.parkingSpaceNumber}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card title={t('dashboard.sessionsCount')} value={typed.length.toString()} />
        <Card title={t('dashboard.energy')} value={formatKwh(totalKwh)} />
        <Card title={t('dashboard.spotCost')} value={formatEur(totals.spotEur)} />
        <Card title={t('dashboard.marginCost')} value={formatEur(totals.marginEur)} />
        <Card title={t('dashboard.cost')} value={formatEur(totalEur)} highlight />
      </div>

      {hasEstimated && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg">
          {t('dashboard.estimatedWarning')}
        </div>
      )}

      {/* Monthly chart */}
      {chartData.length > 0 && <MonthlyChart data={chartData} />}

      {/* Recent sessions */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-sm font-semibold text-brand-600 mb-3">{t('dashboard.recentSessions')}</h2>
        {recent.length === 0 ? (
          <p className="text-brand-400 text-sm">{t('dashboard.noSessions')}</p>
        ) : (
          <div className="space-y-2">
            {recent.map((s, i) => {
              const b = sessionCostBreakdown(s)
              return (
                <div key={i} className="flex justify-between items-center text-sm border-b border-brand-50 pb-2 last:border-0">
                  <div>
                    <span className="text-brand-700 font-medium">{formatDateTimeLocal(s.endTime || s.startTime)}</span>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span className="text-brand-600">{formatKwh(s.totalEnergyKwh)}</span>
                    <span className="text-brand-400 text-xs">{t('dashboard.spotLabel')} {formatEur(b.spotEur)}</span>
                    <span className="text-brand-400 text-xs">+ {t('dashboard.marginLabel')} {formatEur(b.marginEur)}</span>
                    <span className="text-brand-400">=</span>
                    <span className="text-brand-700 font-semibold">{formatEur(s.totalCostEur)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {typed.length > 5 && (
          <a href="/sessions" className="block text-center text-sm text-brand-500 hover:underline mt-3">
            {t('dashboard.showAll')}
          </a>
        )}
      </div>
    </div>
  )
}

type ChartMode = 'kwh' | 'eur'

function MonthlyChart({ data }: { data: { month: string; kwh: number; spotEur: number; marginEur: number; count: number }[] }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<ChartMode>('kwh')

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-brand-600">{t('dashboard.monthlyChart')}</h2>
        <div className="flex gap-2 text-xs">
          <label className={`flex items-center gap-1 cursor-pointer ${mode === 'kwh' ? 'text-brand-700 font-semibold' : 'text-brand-400'}`}>
            <input type="radio" value="kwh" checked={mode === 'kwh'} onChange={() => setMode('kwh')} className="text-brand-500 w-3 h-3" />
            kWh
          </label>
          <label className={`flex items-center gap-1 cursor-pointer ${mode === 'eur' ? 'text-brand-700 font-semibold' : 'text-brand-400'}`}>
            <input type="radio" value="eur" checked={mode === 'eur'} onChange={() => setMode('eur')} className="text-brand-500 w-3 h-3" />
            EUR
          </label>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
          <YAxis yAxisId="main" tick={{ fontSize: 12 }} unit={mode === 'kwh' ? ' kWh' : ' €'} />
          <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 12 }} allowDecimals={false} />
          <Tooltip />
          <Legend />
          {mode === 'kwh' ? (
            <Bar yAxisId="main" dataKey="kwh" name={t('dashboard.energy')} fill="#3a6186" radius={[4, 4, 0, 0]} />
          ) : (
            <>
              <Bar yAxisId="main" dataKey="spotEur" stackId="cost" name={t('dashboard.spotCost')} fill="#3a6186" />
              <Bar yAxisId="main" dataKey="marginEur" stackId="cost" name={t('dashboard.marginCost')} fill="#e8a838" radius={[4, 4, 0, 0]} />
            </>
          )}
          <Line yAxisId="count" dataKey="count" name={t('dashboard.sessionsCount')} stroke="#9ca3af" strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function Card({ title, value, highlight }: { title: string; value: string; highlight?: boolean }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 text-center ${highlight ? 'ring-2 ring-brand-200' : ''}`}>
      <p className="text-brand-400 text-xs uppercase tracking-wide mb-1">{title}</p>
      <p className={`text-2xl font-bold ${highlight ? 'text-brand-800' : 'text-brand-700'}`}>{value}</p>
    </div>
  )
}
